import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useId, useRef, useState } from 'react';
import { financialOverviewApi, type FinancialOverview } from './financial-overview-api';
import { remunerationMoney } from './RemunerationColumn';
import expenseIcon from '../assets/finance/expenses.svg?no-inline';
import settingsIcon from '../assets/finance/settings.svg?no-inline';
import { AnnualExpensesCard } from './AnnualExpensesCard';
import { ExpenseDistributionChart } from './ExpenseDistributionChart';

type Kind = NonNullable<FinancialOverview['expenseCategoryKinds']>[number];
const key = (value: { sourceConnectionId: string; categoryId: string }): string =>
  JSON.stringify([value.sourceConnectionId, value.categoryId]);
const kindNames = {
  fixed: 'Fixe',
  variable: 'Variable',
  mixed: 'Mixte',
  unclassified: 'À préciser',
};

export function ExpenseSection({
  overview,
  csrfToken = '',
  demo = false,
}: {
  readonly overview: FinancialOverview;
  readonly csrfToken?: string;
  readonly demo?: boolean;
}): React.JSX.Element {
  const [period, setPeriod] = useState(overview.period);
  const [loaded, setLoaded] = useState<FinancialOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [setup, setSetup] = useState(false);
  const [demoKinds, setDemoKinds] = useState<readonly Kind[]>(overview.expenseCategoryKinds ?? []);
  useEffect(() => {
    setLoaded(null);
    setError(null);
    if (demo || period === overview.period) return;
    let active = true;
    void financialOverviewApi.read(period).then(
      (value) => {
        if (active) setLoaded(value);
      },
      (reason) => {
        if (active) setError(reason instanceof Error ? reason.message : 'Dépenses indisponibles.');
      },
    );
    return () => {
      active = false;
    };
  }, [overview, period, demo]);
  const source = loaded?.period === period ? loaded : overview;
  const row = source.remuneration?.months.find((month) => month.period === period);
  const categories = row?.expenses ?? [];
  const kinds = demo ? demoKinds : (source.expenseCategoryKinds ?? []);
  const partialBilled = row?.revenue?.complete !== true;
  const billed = source.period === period ? (row?.revenue?.htCents ?? null) : null;
  const payments = [
    ...categories,
    ...(row?.takenCents != null
      ? [
          {
            sourceConnectionId: 'tosho-personal',
            categoryId: 'remuneration',
            name: 'Rémunération dirigeant',
            paidCents: row.takenCents,
            htCents: null,
            personal: true,
          },
        ]
      : []),
  ];
  // La barre représente les paiements positifs : les remboursements sont
  // affichés séparément et ne doivent pas en modifier le dénominateur.
  const paymentsTotal =
    payments.length || row?.expensesComplete === true
      ? payments
          .reduce(
            (sum, entry) => sum + (BigInt(entry.paidCents) > 0n ? BigInt(entry.paidCents) : 0n),
            0n,
          )
          .toString()
      : null;
  const date = new Date(`${period}-01T12:00:00Z`);
  const label = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  return (
    <>
      <article
        className="expense-card finance-card--controls"
        aria-label={`Vos dépenses : ${label}`}
      >
        <header className="finance-card__heading">
          <h3 className="finance-card__title">
            <img src={expenseIcon} alt="" />
            Vos dépenses
          </h3>
          <div className="finance-card__controls">
            <select
              aria-label="Mois des dépenses"
              value={period}
              onChange={(event) => setPeriod(event.currentTarget.value)}
            >
              {(overview.remuneration?.months ?? [])
                .slice()
                .reverse()
                .map((month) => (
                  <option key={month.period} value={month.period}>
                    {new Intl.DateTimeFormat('fr-FR', {
                      month: 'long',
                      year: 'numeric',
                      timeZone: 'UTC',
                    }).format(new Date(`${month.period}-01T12:00:00Z`))}
                  </option>
                ))}
            </select>
            <button
              type="button"
              aria-label="Qualifier les dépenses fixes et variables"
              disabled={!demo && period !== overview.period && loaded?.period !== period}
              onClick={() => setSetup(true)}
            >
              <img src={settingsIcon} alt="" />
            </button>
          </div>
        </header>
        {error ? (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        {!demo && period !== overview.period && !loaded ? (
          error ? null : (
            <p role="status">Chargement des dépenses…</p>
          )
        ) : (
          <>
            <div className="expense-card__summary">
              <p className="expense-card__revenue">
                <span>CA encaissé HT{partialBilled ? ' connu' : ''}</span>
                <b>{remunerationMoney(billed)}</b>
              </p>
              <p className="expense-card__revenue">
                <span>
                  Sorties représentées
                  {row?.expensesComplete !== true || row.takenCents == null ? ' connues' : ''}
                  <small>
                    {row?.takenCents == null
                      ? 'Rémunération non disponible'
                      : 'Rémunération incluse'}{' '}
                    · hors remboursements
                  </small>
                </span>
                <b>{remunerationMoney(paymentsTotal)}</b>
              </p>
            </div>
            <ExpenseDistributionChart
              categories={payments}
              complete={row?.expensesComplete === true && row.takenCents != null}
              showAmounts
              kinds={kinds}
              billedHtCents={billed}
              revenueComplete={!partialBilled && row?.expensesComplete === true}
            />
            {partialBilled ? (
              <p className="expense-card__basis">
                CA incomplet : le sous-total connu est affiché, mais ne sert pas de base aux
                pourcentages. Les parts restent absentes jusqu’à une base complète.
              </p>
            ) : null}
            {row?.expensesComplete !== true ? (
              <p>Les dépenses de ce mois restent à compléter. Les montants connus sont affichés.</p>
            ) : !categories.length ? (
              <p>Aucune sortie catégorisée hors rémunération dans les opérations disponibles.</p>
            ) : null}
            {categories.some((category) => category.htCents === null) ? (
              <p className="expense-card__basis">
                « Payés » indique la sortie réelle lorsque le HT n’est pas fourni. Le ratio au CA
                compare uniquement un montant HT connu au CA encaissé HT ; sinon il reste absent.
              </p>
            ) : null}
          </>
        )}
      </article>
      <AnnualExpensesCard overview={overview} />
      {setup ? (
        <ExpenseKindsDialog
          categories={categories}
          kinds={kinds}
          onCancel={() => setSetup(false)}
          onSave={async (entries) => {
            if (demo) {
              setDemoKinds(entries);
              return;
            }
            await financialOverviewApi.saveExpenseKinds(
              entries,
              source.expenseCategoryKindsVersion ?? null,
              csrfToken,
            );
            const fresh = await financialOverviewApi.read(period);
            setLoaded(fresh);
          }}
        />
      ) : null}
    </>
  );
}

function ExpenseKindsDialog({
  categories,
  kinds,
  onSave,
  onCancel,
}: {
  readonly categories: NonNullable<
    NonNullable<FinancialOverview['remuneration']>['months'][number]['expenses']
  >;
  readonly kinds: readonly Kind[];
  readonly onSave: (kinds: readonly Kind[]) => Promise<void>;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const title = useId();
  const [draft, setDraft] = useState(kinds);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return (
    <ModalDialog
      ref={ref}
      className="treasury-settings expense-settings"
      aria-labelledby={title}
      onCancel={onCancel}
      close={
        <ModalCloseButton className="tosho-modal-close" aria-label="Fermer" onClick={onCancel} />
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSaving(true);
          setError(null);
          void onSave(draft).then(onCancel, (reason) => {
            setError(reason instanceof Error ? reason.message : 'Enregistrement impossible.');
            setSaving(false);
          });
        }}
      >
        <header className="tosho-modal-header">
          <h2 id={title}>Fixe ou variable ?</h2>
        </header>
        <p>
          Une dépense fixe revient même quand votre activité baisse. Une dépense variable suit votre
          activité. Si un type contient les deux, choisissez « Mixte ».
        </p>
        <p>Ce choix est conservé dans Tosho. Votre pointage Freebe reste inchangé.</p>
        {categories.map((category) => (
          <label className="expense-settings__row" key={key(category)}>
            {category.name}
            <select
              value={draft.find((value) => key(value) === key(category))?.kind ?? 'unclassified'}
              onChange={(event) =>
                setDraft([
                  ...draft.filter((value) => key(value) !== key(category)),
                  {
                    sourceConnectionId: category.sourceConnectionId,
                    categoryId: category.categoryId,
                    kind: event.currentTarget.value as Kind['kind'],
                  },
                ])
              }
            >
              {Object.entries(kindNames).map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        ))}
        {error ? (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <footer>
          <button type="button" onClick={onCancel} disabled={saving}>
            Annuler
          </button>
          <button type="submit" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Valider'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
